import React from "react";
import "./model-management.scss";
import { useState, useEffect, Fragment, useContext, useMemo } from "react";
import { Button } from "react-bootstrap";
import { AiOutlinePlus } from "react-icons/ai";
import { BsArrowLeft } from "react-icons/bs";
import { BsLockFill, BsFillUnlockFill } from "react-icons/bs";

import ConfirmationPopup from "../../../shared/components/confirmation-popup/confirmation-popup";
import CustomCheckbox from "../../../shared/components/custom-checkbox/custom-checkbox";
import { ITableRowData, IColumn, IAnyString } from "../../../shared/components/normal-table/normal-table";
import SimpleModal from "../../../shared/components/simple-modal/simple-modal";
import Spinner from "../../../shared/components/spinner/spinner";
import TextInput from "../../../shared/components/text-input/text-input";
import { default as ToolTipGeneralInfo } from "../../../shared/components/tooltip/tooltip";
import WidgetsLayout, { IConfig, IBlock, IOption, IStep, ISettings } from "../../../shared/components/widgets-layout/widgets-layout";
import Wizzard from "../../../shared/components/wizzard/wizzard";
import { roleWrite, roleAdmin } from "../../../shared/constants/user-roles";
import AuthContext from "../../../shared/context/auth-context";
import useHttp from "../../../shared/hooks/use-http";
import { useHttpNonTable } from "../../../shared/hooks/use-httpNonTable";
import { getAllDataNonTable, postSingleNonTable, getAllData } from "../../../shared/utils/api";
import { RenderFields } from "../../../shared/utils/render-fields";
import { historyTrendsAdittionalColums, widgetTypeMoldelManagement, widgetOptions, textWidgetOptionsReport, modelManagementModelDataOverallCols, modelManagementModelDataImportCols, modelManagementModelDataRecordCols, recordTypeOptions, pairedTableslayoutOptions, pairedTableslayoutPositioningOptions } from "../../../shared/utils/static-values";
import { useBack } from "../../../shared/utils/use-back";
import { usePrompt } from "../../../shared/utils/use-prompt";
import { rationToPercents2Decimals, validateLoadingStatusNew, randomHexColorCode, filterArrOfObjByString } from "../../../shared/utils/utils";
import { createUUID } from "../../../shared/utils/utils";
import ModelManagementTableHoc from "./model-management-table-hoc";
import ModelManagementPairedTableHoc from "./model-management-paired-table-hoc";
import ModelManagementChartHoc from "./model-management-chart-hoc";

export interface IParentData {
  parentTabsWidgetData: IBlock[],
  widgetId: string,
}

export interface ISelectedRowsData {
  fullData: ITableRowData[],
  idsOnly: string[]
}

export interface IChartDataset {
  label: string,
  data: string[] | number[] | string,
  borderColor: string,
  backgroundColor: string,
  key: string,
  yAxisID?: string
}

export interface IMultyChart {
  datasets: IChartDataset[],
  labels: string[]
}

export interface IBtnOption {
  name: string,
  id: string
}

export interface IExtractedKey {
  resultKeysExtr: string[]
}

export interface IData {
  className: string,
  items: IBlock[],
  rowHeight: number,
  cols: number,
}

const defaultLayoutSettings = { layout_position: ["horizontal"], layout_ratio: ["1_2"] };

const emptyDefaultModal = {
  id: "",
  read_only: false,
  multi_line: false,
  customColumnsColors: {},
  tabWidgetId: "",
  mainLayoutParent: [],
  selectedStepId: "",
  steps: [],
  name: "",
  textType: "",
  textType_label: "",
  timeframe: undefined,
  dataset: undefined,
  dataset_id: undefined,
  text: "",
  widget_type: "",
  record_type: "",
  info_type: "",
  selectedColumns: [],
  source: "",
  selectedColumnsPaired: [],
  paired_datamodel_id: "",
  paired_datamodel: "",
  widgetLayoutSettings: defaultLayoutSettings
};

const ModelManagement = () => {
  const [unblockInput, setUnblockInput] = useState(false);
  const [draggingStarted, setDraggingStarted] = useState(false);
  const [modal, setModal] = useState(false);
  const [filterByVal, setFilterByVal] = useState("");
  const [lastSelectedPicker, setLastSelectedPicker] = useState("");
  const [resultsColsGeneratedColors, setResultsColsGeneratedColors] = useState<IAnyString | undefined>(undefined);
  const [lock, setLock] = useState(true);

  const [historyColsWithLabelsTransformed, setHistoryColsWithLabelsTransformed] = useState<{ displayName: string, key: string }[]>([]);

  const [modalData, setModalData] = useState<ITableRowData>(emptyDefaultModal);
  const [layout, setLayout] = useState<IConfig[]>([]);
  const [layoutItems, setlayoutItems] = useState<IBlock[]>([]);

  const [isDitry, setIsDirty] = useState(false);
  const { flag, setFlag, next } = usePrompt(isDitry);
  const [gridDefault, setGridDefault] = useState(false);
  const [gridTabsDefault, setGridTabsDefault] = useState(false);

  const authCtx = useContext(AuthContext);

  const { handleBack } = useBack();
  const rolesArray = authCtx.selectedTenant.roles;

  const { sendRequest: postScreen, status: postScreenStatus, data: postScreenData, error: postScreenError } = useHttp(postSingleNonTable, false, authCtx.token, "layout");
  const { sendRequest: getScreen, status: getScreenStatus, data: getScreenData } = useHttpNonTable(getAllDataNonTable, false, authCtx.token, "layout");

  const { sendRequest: getDatamodels, status: getDatamodelsStatus, data: getDatamodelsData } = useHttp(getAllData, false, authCtx.token);
  const { sendRequest: getHistoryCols, status: getHistoryColsStatus, data: getHistoryColsData } = useHttp(getAllData, false, authCtx.token, undefined, "analytics");
  const { sendRequest: getDatasets, status: getDatasetsStatus, data: getDatasetsData } = useHttp(getAllData, true, authCtx.token, undefined, "analytics");

  useEffect(() => {
    if (postScreenStatus === "completed" && postScreenData) {
      setIsDirty(false);
    }
  }, [postScreenStatus]);

  useEffect(() => {
    if (postScreenStatus === "completed" && postScreenData && !postScreenError) {
      next();
    }
  }, [postScreenStatus]);

  useEffect(() => {
    if (getHistoryColsStatus === "completed" && getHistoryColsData) {
      const prepareHistoryColsData = [{
        displayName: "Timeframe",
        key: "timeframe_value"
      }];
      for (const [key, value] of Object.entries(getHistoryColsData.rows.columns)) {
        prepareHistoryColsData.push({ displayName: typeof value === "string" ? value : "", key: key });
      }
      setHistoryColsWithLabelsTransformed(prepareHistoryColsData);
    }
  }, [getHistoryColsStatus]);

  useEffect(() => {
    if (rolesArray.indexOf(roleWrite) !== -1 || rolesArray.indexOf(roleAdmin) !== -1) {
      setUnblockInput(true);
    }
  }, [authCtx.selectedTenant, authCtx.systemAdmin]);

  useEffect(() => {
    authCtx.selectedTenant.selected_tenant_id && getScreen(`template/${authCtx.selectedTenant.selected_tenant_id}`, undefined, undefined, "none");
    getDatasets("datasets");
  }, []);

  useEffect(() => {
    if (getScreenStatus === "completed" && getScreenData) {
      setLayout(getScreenData.layout.layout);
      setlayoutItems(getScreenData.layout.layoutItems);
    }
  }, [getScreenStatus]);

  useEffect(() => {
    let speficToBeFetched = false;
    let historyColsToBeFetched = false;
    layoutItems.forEach((item) => {
      if (
        item.optionName === "Custom widget"
      ) {
        if (item.record_type === "specific") {
          speficToBeFetched = true;
        }
        if (item.settings?.source === "model_performance") {
          historyColsToBeFetched = true;
        }
      }
      else if (item.optionName === "Tabs Widget" && item.steps.length !== 0) {
        item.steps.forEach((step) => {
          step.layoutItems.forEach((item) => {
            if (item.record_type === "specific") {
              speficToBeFetched = true;
            }
            if (item.settings?.source === "model_performance") {
              historyColsToBeFetched = true;
            }
          });
        });
      }
    });
    if (!getDatamodelsData && (speficToBeFetched || modalData.record_type === "specific")) {
      getDatamodels("list_datamodels", undefined, undefined, "none");
    }
    if (!getHistoryColsData && (historyColsToBeFetched || modalData.source === "model_performance")) {
      getHistoryCols("model_performance_columns", undefined, undefined, "none");
    }
  }, [layoutItems, modalData.record_type, modalData.source]);

  const handleSubmit = () => {
    let setId = modalData.id;
    const findIdMatch = layoutItems.filter((item) => item.id === modalData.id);
    const findpairedType = getDatamodelsData?.rows.find((dataModel: ITableRowData) => dataModel.id === modalData.paired_datamodel_id);
    if (modalData.mainLayoutParent.length === 0 && modalData.tabWidgetId === "") {
      if (setId === "") {
        // new main widget
        setId = createUUID();
        setlayoutItems([
          ...layoutItems,
          {
            optionName: modalData.name,
            id: setId,
            text: modalData.text,
            textType: modalData.textType,
            textType_label: modalData?.textType_label,
            read_only: modalData.read_only,
            multi_line: modalData.multi_line,
            steps: modalData.steps,
            record_type: modalData.record_type,
            widgetLayoutSettings: modalData.widgetLayoutSettings,
            info_type: modalData.info_type,
            selectedStepId: modalData.name === "Tabs Widget" ? modalData.steps[0].key : "",
            selectedColumnsPaired: modalData.selectedColumnsPaired ? modalData.selectedColumnsPaired : [],
            settings: modalData.name === "Custom widget" ?
              {
                source: modalData.source ? modalData.source : "",
                paired_datamodel: modalData.paired_datamodel ? modalData.paired_datamodel : "",
                paired_datamodel_type: findpairedType ? findpairedType.type : "",
                paired_datamodel_id: modalData.paired_datamodel_id ? modalData.paired_datamodel_id : "",
                timeframe: modalData.source !== "model_performance" ? undefined : modalData.timeframe,
                dataset: modalData.source !== "model_performance" ? undefined : modalData.dataset,
                dataset_id: modalData.source !== "model_performance" ? undefined : modalData.dataset_id,
                widget_type: modalData.widget_type,
                selectedColumns: modalData.selectedColumns,
                customColumnsColors: modalData.name === "Custom widget" ? (modalData?.customColumnsColors ? { ...modalData.customColumnsColors } : {}) : {},
              }
              : undefined
          },
        ]);
        setIsDirty(true);
      } else {
        // edit main widgets
        let defineOrdering = modalData.selectedColumnsPaired;

        if (defineOrdering && ((modalData.paired_datamodel_id && findIdMatch[0].settings?.paired_datamodel_id && modalData.paired_datamodel_id !== findIdMatch[0].settings?.paired_datamodel_id) ||
          (modalData.record_type && findIdMatch[0].record_type && modalData.record_type !== findIdMatch[0].record_type) ||
          modalData.info_type && findIdMatch[0].info_type && modalData.info_type !== findIdMatch[0].info_type ||
          (modalData.widget_type && findIdMatch[0].settings?.widget_type && modalData.widget_type !== findIdMatch[0].settings?.widget_type))) {
          defineOrdering = [];
        }
        const result = layoutItems.map((item) => {
          if (item.id === setId) {
            return {
              optionName: modalData.name,
              id: setId,
              text: modalData.text,
              textType: modalData.textType,
              read_only: modalData.read_only,
              multi_line: modalData.multi_line,
              textType_label: modalData.textType_label,
              steps: modalData.steps,
              info_type: modalData.info_type,
              widgetLayoutSettings: modalData.widgetLayoutSettings,
              record_type: modalData.record_type,
              selectedStepId: item.selectedStepId,
              selectedColumnsPaired: defineOrdering,
              settings: modalData.name === "Custom widget" ?
                {
                  source: modalData.source ? modalData.source : "",
                  paired_datamodel_type: findpairedType ? findpairedType.type : "",
                  paired_datamodel: modalData.paired_datamodel ? modalData.paired_datamodel : "",
                  paired_datamodel_id: modalData.paired_datamodel_id ? modalData.paired_datamodel_id : "",
                  widget_type: modalData.widget_type,
                  selectedColumns: modalData.selectedColumns,
                  timeframe: modalData.source !== "model_performance" ? undefined : modalData.timeframe,
                  dataset: modalData.source !== "model_performance" ? undefined : modalData.dataset,
                  dataset_id: modalData.source !== "model_performance" ? undefined : modalData.dataset_id,
                  customColumnsColors: modalData.name === "Custom widget" ? (modalData?.customColumnsColors ? { ...modalData.customColumnsColors } : {}) : {},
                }
                : undefined
            };
          } else {
            return item;
          }
        });
        setlayoutItems(result);
        setIsDirty(true);
      }
    } else {
      // from tabs widget
      const result = layoutItems.map((item) => {
        if (item.id === modalData.tabWidgetId) {
          return {
            ...item, steps: item.steps.map((step: IStep) => {
              if (step.key === item.selectedStepId) {
                let layoutItemsResult = [];
                // add the tab's widget
                if (setId === "") {
                  setId = createUUID();
                  layoutItemsResult = [
                    ...step.layoutItems,
                    {
                      optionName: modalData.name,
                      id: setId,
                      text: modalData.text,
                      textType: modalData.textType,
                      read_only: modalData.read_only,
                      multi_line: modalData.multi_line,
                      info_type: modalData.info_type,
                      record_type: modalData.record_type,
                      widgetLayoutSettings: modalData.widgetLayoutSettings,
                      textType_label: modalData.textType_label,
                      steps: [],
                      selectedStepId: modalData.name === "Tabs Widget" ? modalData.steps[0].key : "",
                      selectedColumnsPaired: modalData.selectedColumnsPaired ? modalData.selectedColumnsPaired : [],
                      settings: modalData.name === "Custom widget" ?
                        {
                          paired_datamodel_type: findpairedType ? findpairedType.type : "",
                          source: modalData.source ? modalData.source : "",
                          paired_datamodel: modalData.paired_datamodel ? modalData.paired_datamodel : "",
                          paired_datamodel_id: modalData.paired_datamodel_id ? modalData.paired_datamodel_id : "",
                          widget_type: modalData.widget_type,
                          selectedColumns: modalData.selectedColumns,
                          timeframe: modalData.source !== "model_performance" ? undefined : modalData.timeframe,
                          dataset: modalData.source !== "model_performance" ? undefined : modalData.dataset,
                          dataset_id: modalData.source !== "model_performance" ? undefined : modalData.dataset_id,
                          customColumnsColors: modalData?.customColumnsColors ? { ...modalData.customColumnsColors } : {},
                        }
                        : undefined
                    },
                  ];
                } else {
                  const findmatchIWidgetsOfATab = step.layoutItems.filter((item) => item.id === modalData.id);
                  let defineOrdering = modalData.selectedColumnsPaired;

                  if (defineOrdering && ((modalData.paired_datamodel_id && findmatchIWidgetsOfATab[0].settings?.paired_datamodel_id && modalData.paired_datamodel_id !== findmatchIWidgetsOfATab[0].settings?.paired_datamodel_id) ||
                    (modalData.record_type && findmatchIWidgetsOfATab[0].record_type && modalData.record_type !== findmatchIWidgetsOfATab[0].record_type) ||
                    modalData.info_type && findmatchIWidgetsOfATab[0].info_type && modalData.info_type !== findmatchIWidgetsOfATab[0].info_type ||
                    (modalData.widget_type && findmatchIWidgetsOfATab[0].settings?.widget_type && modalData.widget_type !== findmatchIWidgetsOfATab[0].settings?.widget_type))) {
                    defineOrdering = [];
                  }
                  layoutItemsResult = step.layoutItems.map((layoutItem: IBlock) => {
                    if (setId === layoutItem.id) {
                      return {
                        optionName: modalData.name,
                        text: modalData.text,
                        textType: modalData.textType,
                        read_only: modalData.read_only,
                        info_type: modalData.info_type,
                        multi_line: modalData.multi_line,
                        textType_label: modalData.textType_label,
                        record_type: modalData.record_type,
                        widgetLayoutSettings: modalData.widgetLayoutSettings,
                        id: setId,
                        steps: [],
                        selectedStepId: modalData.selectedStepId,
                        selectedColumnsPaired: defineOrdering,
                        settings: modalData.name === "Custom widget" ?
                          {
                            paired_datamodel_type: findpairedType ? findpairedType.type : "",
                            source: modalData.source ? modalData.source : "",
                            paired_datamodel: modalData.paired_datamodel ? modalData.paired_datamodel : "",
                            paired_datamodel_id: modalData.paired_datamodel_id ? modalData.paired_datamodel_id : "",
                            widget_type: modalData.widget_type,
                            selectedColumns: modalData.selectedColumns,
                            timeframe: modalData.source !== "model_performance" ? undefined : modalData.timeframe,
                            dataset: modalData.source !== "model_performance" ? undefined : modalData.dataset,
                            dataset_id: modalData.source !== "model_performance" ? undefined : modalData.dataset_id,
                            customColumnsColors: modalData?.customColumnsColors ? { ...modalData.customColumnsColors } : {},
                          }
                          : undefined
                      };
                    }
                    return layoutItem;
                  });
                }
                return {
                  ...step, layoutItems: layoutItemsResult
                };
              }
              return step;
            })
          };
        }
        else {
          return item;
        }
      });
      setlayoutItems(result);
      setIsDirty(true);
    }
    setFilterByVal("");
    setModal(false);
    setModalData(emptyDefaultModal);
  };

  const handleModalClosed = () => {
    setFilterByVal("");
    setModal(false);
    setModalData(emptyDefaultModal);
  };

  const onBlockAdd = (name: string, settings: ISettings | undefined) => {
    const newID = createUUID();
    const withNewItem = [
      ...layoutItems,
      {
        optionName: name,
        id: newID,
        settings,
        steps: []
      },
    ];
    setlayoutItems(withNewItem);
    setIsDirty(true);
  };

  const getNewColumnsOrderPaired = (data: IParentData, order: IColumn[]) => {
    const onlyKeys = order.map((column: IColumn) => column.key);
    if (data.parentTabsWidgetData.length === 0) {
      const layoutItemsClone: IBlock[] = layoutItems.map((item) => {
        if (item.id === data.widgetId) {
          return { ...item, selectedColumnsPaired: onlyKeys, settings: item.settings ? { ...item.settings } : undefined };
        }
        return item;
      });
      setlayoutItems(layoutItemsClone);
      setIsDirty(true);
    } else {
      // Tabs widget
      const layoutItemsClone: IBlock[] = layoutItems.map((item) => {
        if (item.id === data.parentTabsWidgetData[0].id) {
          return {
            ...item, steps: item.steps.map((step) => {
              if (item.selectedStepId === step.key) {
                return {
                  ...step, layoutItems: step.layoutItems.map((layoutItem) => {
                    if (data.widgetId === layoutItem.id) {
                      return { ...layoutItem, selectedColumnsPaired: onlyKeys };
                    }
                    return item;
                  })
                };
              }
              return step;
            })
          };
        }
        return item;
      });
      setlayoutItems(layoutItemsClone);
      setIsDirty(true);
    }
  };

  const getNewColumnsOrder = (data: IParentData, order: IColumn[]) => {
    const onlyKeys = order.map((column: IColumn) => column.key);
    if (data.parentTabsWidgetData.length === 0) {
      const layoutItemsClone: IBlock[] = layoutItems.map((item) => {
        if (item.id === data.widgetId) {
          return { ...item, settings: item.settings ? { ...item.settings, selectedColumns: onlyKeys } : undefined };
        }
        return item;
      });
      setlayoutItems(layoutItemsClone);
      setIsDirty(true);
    } else {
      // Tabs widget
      const layoutItemsClone: IBlock[] = layoutItems.map((item) => {
        if (item.id === data.parentTabsWidgetData[0].id) {
          return {
            ...item, steps: item.steps.map((step) => {
              if (item.selectedStepId === step.key) {
                return {
                  ...step, layoutItems: step.layoutItems.map((layoutItem) => {
                    if (data.widgetId === layoutItem.id) {
                      return { ...layoutItem, settings: layoutItem.settings ? { ...layoutItem.settings, selectedColumns: onlyKeys } : undefined };
                    }
                    return item;
                  })
                };
              }
              return step;
            })
          };
        }
        return item;
      });
      setlayoutItems(layoutItemsClone);
      setIsDirty(true);
    }
  };

  const defineOptions = (name: string) => {
    const result: any = { // eslint-disable-line
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index" as const, intersect: false },
      stacked: false,
      clip: false,
      plugins: {
        title: {
          display: true,
          text: name,
        },
        tooltip: {
          usePointStyle: true,
          callbacks: {
            title: (data: any) => { // eslint-disable-line
              return name === "Results" ? `Threshold: ${data[0].label}` : data[0].label;
            },
            label: function (context: any) { // eslint-disable-line
              let label = context.dataset.label;
              if (label) {
                label += ": ";
              }
              if (context.parsed.y !== null) {
                if (
                  context.dataset.label.includes("Percentage") ||
                  context.dataset.label.includes("ratio")
                ) {
                  label += rationToPercents2Decimals(context.parsed.y);
                } else {
                  label += context.parsed.y;
                }
              }
              return label;
            }
          }
        }
      },
      scales: {
        y: {
          type: "linear" as const,
          display: true,
          position: "left" as const,
          min: 0
        },
        y1: {
          type: "linear" as const,
          max: 1,
          min: 0,
          ticks: {
            beginAtZero: true,
            count: 11,
            callback: ((value: any) => { // eslint-disable-line
              return rationToPercents2Decimals(value);
            })
          },
          display: true,
          position: "right" as const,
          grid: {
            drawOnChartArea: false,
          },
        },
      },
    };
    return result;
  };

  const renderCustomWidget = (id: string, layoutItemsLoc: IBlock[], layoutLoc: IConfig[], tabsWidgetData?: IBlock[]) => {
    const findIdMatch = layoutItemsLoc.find((item: IBlock) => item.id === id);

    if (!findIdMatch) {
      return <div>loading...</div>;
    }
    const findScaleinLayout = layoutLoc.filter((block: IConfig) => block.i === id);

    if (findIdMatch.settings?.widget_type === "pairing_tables" && findIdMatch.settings?.source === "data_quality") {
      return (
        <ModelManagementPairedTableHoc
          orderSettings={{ parentTabsWidgetData: tabsWidgetData ? tabsWidgetData : [], widgetId: id }}
          draggingStarted={draggingStarted}
          getDraggingStatus={(toggle: boolean) => setDraggingStarted(toggle)}
          widgetData={findIdMatch}
          allColumns={undefined}
          getNewColumnsOrder={getNewColumnsOrder}
          getNewColumnsOrderPaired={getNewColumnsOrderPaired}
          allStatus="completed"
          dataUrl={"noPropagation"}
          defaultSortCol={"name"}
          hideSearchInput={true}
          hideCheckBoxes={true}
          unblockInput={unblockInput}
          clickRowToSelectMode={false}
          hideButtons={[]}
          scale={{ height: `${findScaleinLayout.length !== 0 ? ((findScaleinLayout[0].h * 25) - 10) : 180}`, width: "auto" }}
        />
      );
    } else if (findIdMatch.settings?.widget_type === "table" && findIdMatch.settings?.source === "model_performance") {
      return (
        <ModelManagementTableHoc
          orderSettings={{ parentTabsWidgetData: tabsWidgetData ? tabsWidgetData : [], widgetId: id }}
          draggingStarted={draggingStarted}
          getDraggingStatus={(toggle: boolean) => setDraggingStarted(toggle)}
          widgetData={findIdMatch}
          allColumns={historyColsWithLabelsTransformed}
          getNewColumnsOrder={getNewColumnsOrder}
          allStatus="completed"
          dataUrl={"noPropagation"}
          defaultSortCol={"first_day_of_week"}
          hideSearchInput={true}
          hideCheckBoxes={true}
          unblockInput={unblockInput}
          clickRowToSelectMode={false}
          hideButtons={[]}
          scale={{ height: `${findScaleinLayout.length !== 0 ? ((findScaleinLayout[0].h * 25) - 10) : 180}`, width: "auto" }}
        />
      );
    } else if (findIdMatch.settings?.widget_type === "chart" && findIdMatch.settings?.source === "model_performance") {
      const applySelectedColor = (data: IMultyChart) => {
        return {
          ...data,
          datasets: data.datasets.map((col: IChartDataset) => {
            const findSelectedColor = findIdMatch.settings?.customColumnsColors?.[col.key];
            const colorResult = findSelectedColor ? findSelectedColor : col.backgroundColor;
            return { ...col, backgroundColor: colorResult, borderColor: colorResult };
          })
        };
      };

      return <ModelManagementChartHoc allHistoryCols={getHistoryColsData} widgetData={findIdMatch} options={defineOptions("Model performance")} applySelectedColor={applySelectedColor} />;
    }
  };

  const renderWizard = (id: string) => {
    const findIdMatch = layoutItems.filter((item) => item.id === id);
    if (!findIdMatch[0]) {
      return <div>loading...</div>;
    }
    const selectedTabIndex = findIdMatch[0].steps.findIndex((step: IStep) => step.key === findIdMatch[0].selectedStepId);
    const selectedTabData = selectedTabIndex !== -1 ? findIdMatch[0].steps[selectedTabIndex] : findIdMatch[0].steps[0];
    const constructTabs = () => findIdMatch[0].steps.map((step: IStep, i: number) => (
      {
        index: i + 1,
        stepName: step.name,
        id: step.key,
        fields: [],
      }
    ));

    const getLayoutChangesWizard = (layoutLoc: IConfig[]) => {
      let result = [...layoutItems];
      result = result.map((item) => {
        if (findIdMatch[0].id === item.id) {
          return {
            ...item,
            steps: item.steps.map((step: IStep) => {
              if (findIdMatch[0].selectedStepId === step.key) {
                return {
                  ...step,
                  layout: layoutLoc
                };
              }
              return step;
            })
          };
        }
        return item;
      });
      setlayoutItems(result);

      if (!gridTabsDefault) {
        setGridTabsDefault(true);
      } else {
        setIsDirty(true);
      }
    };

    const getLayoutItemsWizard = (layoutItemsLoc: IBlock[], i: string) => {
      let result = [...layoutItems];
      result = result.map((item) => {
        if (findIdMatch[0].id === item.id) {
          return {
            ...item,
            steps: item.steps.map((step: IStep) => {
              if (findIdMatch[0].selectedStepId === step.key) {
                return {
                  ...step,
                  layoutItems: layoutItemsLoc,
                  layout: selectedTabData.layout.filter((item: IConfig) => item.i !== i)
                };
              }
              return step;
            })
          };
        }
        return item;
      });
      setlayoutItems(result);
      setIsDirty(true);
    };

    const renderTabsWidget = () => {
      return (
        <WidgetsLayout
          layout={selectedTabData ? selectedTabData.layout : []}
          layoutItems={selectedTabData ? selectedTabData.layoutItems : []}
          mainLayoutParent={findIdMatch}
          getLayoutChanges={getLayoutChangesWizard}
          getLayoutItems={getLayoutItemsWizard}
          optionsBlocks={optionsBlocks}
          lock={lock}
          setModal={setModal}
          setModalDataFromBlock={setModalDataFromBlock}
          unblockInput={unblockInput}
          tabsWidget={true}
          key={selectedTabIndex}
        />
      );
    };

    return (
      <Wizzard
        isFullscreen={true}
        noNumeration={true}
        allowedTabs={findIdMatch[0].steps.map((step: IStep, i: number) => i + 1)}
        selectedTabFromParent={selectedTabIndex !== -1 ? selectedTabIndex + 1 : 1}
        getSelectedTab={(tab: number, tabId?: string) => setlayoutItems(layoutItems.map((item) => item.id === id ? { ...item, selectedStepId: tabId } : item))}
        data={constructTabs()}
      >
        {renderTabsWidget()}
      </Wizzard>
    );
  };

  const renderText = (id: string, layoutItemsLoc: IBlock[]) => {
    const findIdMatch: IBlock | undefined = layoutItemsLoc?.find((item: IBlock) => item.id === id);
    if (!findIdMatch || !findIdMatch?.textType) {
      return <div>loading...</div>;
    }
    if (findIdMatch.textType === "text") {
      return <p className="reportDetails-labelWrapper">{findIdMatch.text}</p>;
    } else if (findIdMatch.textType === "header") {
      return <h2 className="reportDetails-labelWrapper">{findIdMatch.text}</h2>;
    }
  };

  const optionsBlocks = [
    {
      name: "Text",
      displayName: "Text",
      component: renderText,
      settings: undefined,
      editable: true
    },

    {
      name: "Tabs Widget",
      displayName: "Tabs widget",
      component: renderWizard,
      settings: undefined,
      editable: true
    },

    {
      name: "Custom widget",
      displayName: "Add widget",
      component: renderCustomWidget,
      settings: undefined,
      editable: true
    }
  ];

  const getLayoutChanges = (layout: IConfig[]) => {
    setLayout(layout);

    !gridDefault && setGridDefault(true);
    if (gridDefault) {
      setIsDirty(true);
    }
  };

  const onModalClickHandler = (event: React.MouseEvent<HTMLElement>) => {
    const filteredId = (event.target as Element).id.replace("arrow-", "");

    if (filteredId !== lastSelectedPicker && lastSelectedPicker) {
      setLastSelectedPicker(filteredId);
    }
    else if (filteredId !== lastSelectedPicker && (event.target as Element).className !== "react-colorful__interactive" && lastSelectedPicker !== "") {
      setLastSelectedPicker("");
    }
  };

  const handleOnChange = (targetVal: string, inputKey: string, id: string) => {
    if (modalData.name === "Tabs Widget") {
      if (modalData.steps.length === 0) {
        // first step
        setModalData((prevState) => ({ ...prevState, steps: [{ key: inputKey, name: targetVal, layout: [], layoutItems: [] }] }));
      } else {
        // edit
        let matched = false;
        let stepsData = modalData.steps.map((step: IStep) => {
          if (step.key === inputKey) {
            matched = true;
            return { key: inputKey, name: targetVal, layout: step.layout, layoutItems: step.layoutItems };
          } else {
            return step;
          }
        });
        if (!matched) {
          // no match => add a new step
          stepsData = [...stepsData, { key: inputKey, name: targetVal, layout: [], layoutItems: [] }];
        }
        setModalData((prevState) => ({ ...prevState, steps: stepsData }));
      }
    } else {
      if (inputKey === "source") {
        setModalData({ ...emptyDefaultModal, steps: modalData.steps, tabWidgetId: modalData.tabWidgetId, [inputKey]: targetVal, name: modalData.name, id: modalData.id });
      }
      if (inputKey === "info_type") {
        setModalData({ ...modalData, [inputKey]: targetVal, selectedColumnsPaired: [], selectedColumns: [] });
      }
      else if (inputKey === "record_type" && targetVal !== "specific") {
        setModalData({ ...modalData, [inputKey]: targetVal, paired_datamodel: "", paired_datamodel_id: "" });
      }
      else if (inputKey === "paired_datamodel") {
        setModalData({ ...modalData, paired_datamodel: targetVal, paired_datamodel_id: id });
      }
      else if (
        ((inputKey === "widget_type" && targetVal !== modalData.widget_type && modalData.widget_type !== "") ||
          (inputKey === "record_type" && targetVal !== modalData.record_type && modalData.record_type !== ""))
      ) {
        // when switching record_type or type, empty selected colums
        setModalData({ ...modalData, [inputKey]: targetVal });
      } else {
        if (inputKey === "dataset") {
          setModalData((prevState) => ({ ...prevState, [inputKey]: targetVal, dataset_id: id }));
        } else {
          setModalData((prevState) => ({ ...prevState, [inputKey]: targetVal }));
        }
      }
    }
  };

  const handleOnSelectColumn = (key: string) => {
    let result = [...modalData.selectedColumns];
    if (result.indexOf(key) === -1) {
      if (!resultsColsGeneratedColors?.[key]) {
        setResultsColsGeneratedColors({ ...resultsColsGeneratedColors, [key]: randomHexColorCode() });
      }
      result = [...result, key];
    } else {
      result = result.filter((selectedKey) => selectedKey !== key);
    }
    setModalData({ ...modalData, selectedColumns: result });
  };

  const handleOnSelectColumnPaired = (key: string) => {
    let result = [...modalData.selectedColumnsPaired];
    if (result.indexOf(key) === -1) {
      result = [...result, key];
    } else {
      result = result.filter((selectedKey) => selectedKey !== key);
    }
    setModalData({ ...modalData, selectedColumnsPaired: result });
  };

  const handleOnSelectColumnColor = (key: string, color: string, defaultColor?: undefined | string) => {
    setModalData({ ...modalData, customColumnsColors: { ...modalData.customColumnsColors, [key]: color ? color : defaultColor } });
  };

  const widgetColumnsBtns = (source?: string, mode?: string) => {
    let allMetrics = [
      [
        {
          metricColId: "layout_position",
          metrics: [...pairedTableslayoutPositioningOptions],
          excludeOnSelect: ["horizontal", "vertical"]
        },
      ]
    ];

    if (modalData.info_type !== "overall" || modalData.record_type !== "specific") {
      allMetrics = [[
        {
          metricColId: "layout_ratio",
          metrics: [...pairedTableslayoutOptions],
          excludeOnSelect: ["1_2", "2_3", "none"]
        },
      ], ...allMetrics];
    }

    if (source === "layoutSelecting") {
      return allMetrics.map((metricCol, i) => {
        return (
          <div key={i} className="reportDetails-aggregateBtns1">
            {metricCol.map((metricData, i) => (
              <div key={i}>
                {metricData.metrics.map((optionData, i) => {
                  return (
                    <CustomCheckbox
                      simple={true}
                      key={i}
                      noEmptySelection={modalData.widgetLayoutSettings[`${metricData.metricColId}`].indexOf(optionData.id) !== -1 ? true : false}
                      handleOnSelectRole={() => {
                        if (modalData.widgetLayoutSettings?.[`${metricData.metricColId}`].indexOf(optionData.id) === -1) {
                          const result = modalData.widgetLayoutSettings?.[`${metricData.metricColId}`].filter((opt: string) => {
                            return metricData.excludeOnSelect.indexOf(opt) === -1;
                          });
                          setModalData({ ...modalData, widgetLayoutSettings: { ...modalData.widgetLayoutSettings, [`${metricData.metricColId}`]: [...result, optionData.id] } });
                        }
                      }}
                      id={optionData.id}
                      text={optionData.name}
                      initialValue={modalData.widgetLayoutSettings[`${metricData.metricColId}`].indexOf(optionData.id) !== -1}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        );
      });
    }
    if (source === "columnsSelection") {
      const defineImportOrOveralCols = modalData.info_type === "overall" ? modelManagementModelDataOverallCols : modelManagementModelDataImportCols;
      return defineImportOrOveralCols.map((colData, i) => {
        return (
          <CustomCheckbox
            simple={true}
            key={i}
            handleOnSelectRole={handleOnSelectColumnPaired}
            id={colData.key}
            text={colData.displayName}
            initialValue={modalData.selectedColumnsPaired?.indexOf(colData.key) !== -1}
          />
        );
      });
    } else if (source === "columnsPairedSelection") {
      return modelManagementModelDataRecordCols.map((colData, i) => {
        return (
          <CustomCheckbox
            simple={true}
            key={i}
            handleOnSelectRole={handleOnSelectColumn}
            id={colData.key}
            text={colData.displayName}
            initialValue={modalData.selectedColumns?.indexOf(colData.key) !== -1}
          />
        );
      });
    } else if (source === "columnsSelectionHistory" && historyColsWithLabelsTransformed) {
      let prepareOnlySelectedCols = historyColsWithLabelsTransformed.filter(c => c.key !== "timeframe_value");

      if (mode === "onlySelected") {
        prepareOnlySelectedCols = prepareOnlySelectedCols.filter((colData) => {
          return modalData.selectedColumns.indexOf(colData.key) !== -1;
        });
      }

      prepareOnlySelectedCols = filterByVal ? filterArrOfObjByString(prepareOnlySelectedCols, "key", filterByVal) : prepareOnlySelectedCols;

      return prepareOnlySelectedCols.map((colData) => {
        return (
          <CustomCheckbox
            withInnerHeight={"#modalInputsWrapper"}
            lastSelectedPicker={lastSelectedPicker}
            setLastSelectedPicker={(keyId: string) => setLastSelectedPicker(keyId)}
            handleOnSelectColumnColor={handleOnSelectColumnColor}
            selectedColor={modalData.customColumnsColors[colData.key] ? modalData.customColumnsColors[colData.key] : ""}
            defaultColor={resultsColsGeneratedColors ? resultsColsGeneratedColors[colData.key] : ""}
            initialValue={modalData.selectedColumns.indexOf(colData.key) !== -1}
            simple={true}
            key={colData.displayName}
            handleOnSelectRole={handleOnSelectColumn}
            id={colData.key}
            text={colData.displayName}
          />
        );
      });
    }
  };

  const memorizedPrint = useMemo(() => {
    if (Object.keys(modalData).length !== 0 && modalData.constructor === Object) {
      let modalFields: IColumn[] = [];
      let stepsData: any = {}; // eslint-disable-line 

      if (modalData.name === "Tabs Widget") {
        if (modalData.steps.length === 0) {
          // initial step
          const initialStepKey = createUUID();
          setModalData({ ...modalData, steps: [...modalData.steps, { key: initialStepKey, name: "", layout: [], layoutItems: [] }] });
          modalFields = [
            {
              key: initialStepKey,
              displayName: "Tab 1",
              dataType: {
                type: "text",
              }
            },
          ];
        } else {
          modalFields = modalData.steps.map((step: IStep, i: number) => {
            stepsData = { ...stepsData, [step.key]: step.name };
            return {
              key: step.key,
              displayName: `Tab ${i + 1}`,
              dataType: {
                type: "text",
              }
            };
          });
        }
      } else if (modalData.name === "Text") {
        modalFields = [
          {
            key: "textType",
            displayName: "Text type",
            dataType: {
              type: "enumeration",
              values: textWidgetOptionsReport
            }
          },
        ];
        if (modalData.textType === "text") {
          modalFields = [
            ...modalFields,
            {
              key: "text",
              displayName: "Text",
              dataType: {
                type: "textarea",
              }
            },
          ];
        } else if (modalData.textType === "header") {
          modalFields = [
            ...modalFields,
            {
              key: "text",
              displayName: "Text",
              dataType: {
                type: "text",
              }
            },
          ];
        }
      } else if (modalData.name === "Custom widget") {
        modalFields = [
          {
            key: "source",
            displayName: "Source",
            dataType: {
              type: "enumeration",
              values: [{ name: "Data quality", id: "data_quality" }, { name: "Model performance", id: "model_performance" }]
            },
            extraClass: "half"
          },
          {
            key: "widget_type",
            displayName: "Widget type",
            dataType: {
              type: "enumeration",
              values: modalData.source === "model_performance" ? widgetOptions : [{ name: "Pairing tables", id: "pairing_tables" }]
            },
            extraClass: "half"
          }
        ];
      }
      else if (modalData.tabWidgetId) {
        modalFields = [
          {
            key: "name",
            displayName: "Widget",
            dataType: {
              type: "enumeration",
              values: widgetTypeMoldelManagement
            }
          },
          ...modalFields
        ];
      }

      if (modalData.source === "data_quality") {
        modalFields = [
          ...modalFields,
          {
            key: "record_type",
            displayName: "Datamodel type",
            dataType: {
              type: "enumeration",
              values: recordTypeOptions
            },
            extraClass: "half"
          },
          {
            key: "info_type",
            displayName: "Info type",
            dataType: {
              type: "enumeration",
              values: [{ name: "Overall", id: "overall" }, { name: "Importing", id: "importing" }]
            },
            extraClass: "half"
          },
        ];
      }

      let additionalModalPerfomanceCols: IColumn[] = [];

      if (modalData.source === "model_performance") {
        additionalModalPerfomanceCols = [
          ...historyTrendsAdittionalColums,
          {
            key: "dataset",
            displayName: "Dataset",
            dataType: {
              type: "reference",
              values: getDatasetsData ? getDatasetsData.rows.map((row: ITableRowData) => { return { id: row.id, name: row.dataset_name }; }) : [],
            },
            placeholder: "select a dataset",
            extraClass: "half"
          }
        ];
      }

      if (modalData.widget_type === "pairing_tables" && modalData.record_type === "specific") {
        modalFields = [
          ...modalFields,
          {
            key: "paired_datamodel",
            displayName: "Datamodel",
            dataType: {
              type: "reference",
              values: getDatamodelsData ? getDatamodelsData.rows : []
            }
          }
        ];
      }

      return (
        <Fragment>
          <div onScroll={() => setLastSelectedPicker("")} style={{ paddingRight: `${modalData.steps.length >= 6 ? "15px" : ""}` }} id="modalInputsWrapper" className="reportDetails-modalInputsWrapper">
            {(unblockInput && !lock) && <RenderFields
              deleteInput={modalData.name === "Tabs Widget" ? (id: string) => {
                setModalData({ ...modalData, steps: modalData.steps.filter((step: IStep) => step.key !== id) });
              } : undefined}
              fields={[...modalFields]} data={modalData.name === "Tabs Widget" ? stepsData : modalData}
              handleOnChange={handleOnChange}
            />}

            {modalData.source === "model_performance" ? <RenderFields
              fields={[...additionalModalPerfomanceCols]} data={modalData.name === "Tabs Widget" ? stepsData : modalData}
              handleOnChange={handleOnChange}
            /> : ""}

            {(unblockInput && !lock) && <Fragment>
              {modalData.name === "Tabs Widget" && <Button
                name="add"
                onClick={() => setModalData({ ...modalData, steps: [...modalData.steps, { key: createUUID(), name: "", layout: [], layoutItems: [] }] })}
                className="normalTable-new"
                variant="primary"
              >
                <AiOutlinePlus />
              </Button>}

              {(modalData.source && modalData.source === "model_performance") && <TextInput
                type={"text"}
                extraClass={modalData.source === "history" ? "half" : ""}
                id={"reportDetails-searchUserInput"}
                required={false}
                inputKey={"filterColumnsWith"}
                autofocus={false}
                placeholder={"Type to filter"}
                handleOnChange={(targetVal: string) => setFilterByVal(targetVal)}
              />}

              {(modalData.name === "Custom widget" && modalData.widget_type === "pairing_tables" && modalData.source === "data_quality") && <Fragment>
                <div>
                  <div className="reportDetails-aggregateBtns1">
                    <div className="metricsPoolTitle">Select columns:</div>
                    {widgetColumnsBtns("columnsSelection")}
                  </div>
                  <div className="reportDetails-aggregateBtns1">
                    <div className="metricsPoolTitle">Select paired columns:</div>
                    {widgetColumnsBtns("columnsPairedSelection")}
                  </div>
                </div>
                <div>
                  <div className="metricsPoolTitle">Selected a layout: </div>
                  {widgetColumnsBtns("layoutSelecting")}
                </div>
              </Fragment>}

              {(modalData.name === "Custom widget" && modalData.source === "model_performance") && <Fragment>
                <div>
                  <div className="reportDetails-aggregateBtns1">
                    <div className="metricsPoolTitle">Select columns:</div>
                    {widgetColumnsBtns("columnsSelectionHistory")}
                  </div>

                  <span className={"reportDetails-selectedColumsPoolWrapper"}>
                    <div className={"metricsPoolTitle"}>Selected metrics: </div>
                    {modalData.selectedColumns.length !== 0 ? widgetColumnsBtns("columnsSelectionHistory", "onlySelected") : <span className="reportDetails-noMetricsMsgs">No metrics have been selected</span>}
                  </span>
                </div>
              </Fragment>}
            </Fragment>}

          </div>

        </Fragment>
      );
    }
  }, [modalData, filterByVal, lastSelectedPicker, resultsColsGeneratedColors, getDatamodelsStatus, setLastSelectedPicker]);

  const setModalDataFromBlock = (id: string, layoutItemsLoc: IBlock[], mainLayoutParent: IBlock[], newTabWidget: boolean, tabWidgetEdit?: string) => {
    const findItemBlock = layoutItemsLoc.filter((block: IBlock) => block.id === id);
    setModalData({
      mainLayoutParent,
      customColumnsColors: findItemBlock[0].settings?.customColumnsColors ? { ...findItemBlock[0].settings?.customColumnsColors } : {},
      id: newTabWidget ? "" : id,
      tabWidgetId: newTabWidget ? id : (tabWidgetEdit ? tabWidgetEdit : ""),
      steps: findItemBlock[0].steps,
      name: newTabWidget ? "" : findItemBlock[0].optionName,
      info_type: findItemBlock[0].info_type ? findItemBlock[0].info_type : "",
      record_type: findItemBlock[0].record_type ? findItemBlock[0].record_type : "",
      textType: findItemBlock[0].textType ? findItemBlock[0].textType : "",
      source: findItemBlock[0].source ? findItemBlock[0].source : "",
      read_only: findItemBlock[0].read_only ? findItemBlock[0].read_only : false,
      multi_line: findItemBlock[0].multi_line ? findItemBlock[0].multi_line : false,
      textType_label: findItemBlock[0].textType_label ? findItemBlock[0].textType_label : "",
      text: findItemBlock[0].text ? findItemBlock[0].text : "",
      timeframe: findItemBlock[0].timeframe ? undefined : modalData.timeframe,
      dataset: findItemBlock[0].dataset ? undefined : modalData.dataset,
      dataset_id: findItemBlock[0].dataset_id ? undefined : modalData.dataset_id,
      selectedColumns: findItemBlock[0].selectedColumns ? findItemBlock[0].selectedColumns : [],
      selectedColumnsPaired: findItemBlock[0].selectedColumnsPaired ? findItemBlock[0].selectedColumnsPaired : [],
      widgetLayoutSettings: findItemBlock[0].widgetLayoutSettings ? findItemBlock[0].widgetLayoutSettings : defaultLayoutSettings,
      ...findItemBlock[0].settings
    });
  };

  const renderOptionsBtns = (options: IOption[]) => {
    return options.map((option: IOption, i: number) => {
      return (
        <Button key={i} className="blank" onClick={() => {
          if (option.name === "Custom widget" || option.name === "Text" || option.name === "Tabs Widget") {
            setModal(true);
            // set the name of the unique widgets
            setModalData({ ...modalData, name: option.name });
          } else {
            onBlockAdd(option.name, option.settings);
          }
        }}>
          {option.displayName} +
        </Button>
      );
    });
  };

  const handleScreenSave = () => {
    const screenId = authCtx.selectedTenant.selected_tenant_id ? authCtx.selectedTenant.selected_tenant_id : createUUID();
    postScreen(`template/${screenId}`, { id: screenId, name: authCtx.selectedTenant.selected_tenant, layout: { layout: (layout), layoutItems: layoutItems } });
  };

  return (
    <div className="modelManagement">
      {flag && <ConfirmationPopup show={flag} handleSaveClick={handleScreenSave} handlePopupClosed={() => setFlag(false)} handleContinue={() => { next(); setFlag(false); }} />}
      <SimpleModal
        wrapperClass={"modelManagementModal"}
        onModalClickHandler={onModalClickHandler}
        modalBody={memorizedPrint}
        buttonText={"Save"}
        mode={`${modalData.name ? modalData.name : "Tabs"} settings`}
        handleModalClosed={handleModalClosed}
        handleSubmit={handleSubmit}
        show={modal}
      />

      {validateLoadingStatusNew([postScreenStatus, getDatasetsStatus, getScreenStatus, getDatamodelsStatus]) && <Spinner />}
      <span>
        <div onClick={() => handleBack()} className="listPage-nav-btn">
          <BsArrowLeft />
        </div>
        <div className="modelManagement-header">
          <h1>Data monitoring</h1>
        </div>
      </span>

      <div className="modelManagement-btnWrapper">
        {!lock && renderOptionsBtns(optionsBlocks)}

        {!lock && <ToolTipGeneralInfo
          content={"Lock the editor"}
          direction="top"
          delay={350}
        >
          <Button
            disabled={!unblockInput}
            name="save"
            onClick={() => {
              setLock(!lock);
              !lock && handleScreenSave();
            }}
            className="blank"
          >
            <BsFillUnlockFill />
          </Button>
        </ToolTipGeneralInfo>}
        {lock && <ToolTipGeneralInfo
          content={"Unlock the editor"}
          direction="top"
          delay={350}
        >
          <Button
            disabled={!unblockInput}
            onClick={() => {
              setLock(!lock);
            }}
            className="blank"
          >
            <BsLockFill />
          </Button>
        </ToolTipGeneralInfo>}

      </div>
      {(layoutItems.length !== 0) ?
        <WidgetsLayout
          layout={layout}
          layoutItems={layoutItems}
          getLayoutChanges={getLayoutChanges}
          getLayoutItems={(data) => setlayoutItems(data)}
          optionsBlocks={optionsBlocks}
          lock={lock}
          setModal={setModal}
          setModalDataFromBlock={setModalDataFromBlock}
          unblockInput={unblockInput}
          mainLayoutParent={[]}

        />
        :
        <div>
          <h5>No Widgets Available</h5>
          <p>
            It looks like you don't have any widgets created yet. Unlock the editor to start building your first widget and customize your data monitoring experience.
          </p>
        </div>
      }

    </div>
  );
};

export default ModelManagement;